// Single source of truth for which permission(s) gate each Maintenance (master data) tab —
// used by the sidebar nav filter, the route guard, and the tab list/actions inside the
// Maintenance shell (views/maintenance/index.tsx). Tabs are matched by their `:tab` path
// param slug.
//
// Each tab has a manage/read pair (view the tab if either is held) plus separate import/export
// permissions for its Import/Export actions — these are independent, explicitly grantable
// permissions in the backend, not implied by manage/read. `importPermission`/`exportPermission`
// are only set for tabs that actually have a working Import/Export action in the UI today.
//
// Material Categories has no dedicated permission codes of its own — it reuses the Products
// permissions, matching how MaterialCategoriesController.cs is already gated server-side
// ([HasPermission(Permissions.ManageProducts)]).

export const MAINTENANCE_ROUTE = '/maintenance';

export interface MaintenanceTabAccess {
  slug: string;
  label: string;
  managePermission: string;
  readPermission: string;
  importPermission?: string;
  exportPermission?: string;
}

export const MAINTENANCE_TABS: MaintenanceTabAccess[] = [
  {
    slug: 'vehicles',
    label: 'Vehicles',
    managePermission: 'manage:vehicles',
    readPermission: 'read:vehicles',
    importPermission: 'import:vehicles',
    exportPermission: 'export:vehicles'
  },
  {
    slug: 'materials',
    label: 'Materials',
    managePermission: 'manage:products',
    readPermission: 'read:products',
    importPermission: 'import:products',
    exportPermission: 'export:products'
  },
  {
    slug: 'customers',
    label: 'Customers',
    managePermission: 'manage:customers',
    readPermission: 'read:customers',
    importPermission: 'import:customers',
    exportPermission: 'export:customers'
  },
  {
    slug: 'contracts',
    label: 'Contracts',
    managePermission: 'manage:contracts',
    readPermission: 'read:contracts',
    importPermission: 'import:contracts',
    exportPermission: 'export:contracts'
  },
  {
    slug: 'carriers',
    label: 'Carriers',
    managePermission: 'manage:carriers',
    readPermission: 'read:carriers',
    importPermission: 'import:carriers',
    exportPermission: 'export:carriers'
  },
  {
    slug: 'vehicle-types',
    label: 'Vehicle Types',
    managePermission: 'manage:vehicle_types',
    readPermission: 'read:vehicle_types',
    exportPermission: 'export:vehicle_types'
  },
  {
    slug: 'containers',
    label: 'Containers',
    managePermission: 'manage:containers',
    readPermission: 'read:containers',
    exportPermission: 'export:containers'
  },
  {
    // Container types have no permission codes of their own — they reuse the container permissions,
    // matching how ContainerTypesController.cs is gated server-side
    // ([HasPermission(Permissions.ManageContainers)]).
    slug: 'container-types',
    label: 'Container Types',
    managePermission: 'manage:containers',
    readPermission: 'read:containers',
    exportPermission: 'export:containers'
  },
  {
    slug: 'material-categories',
    label: 'Material Categories',
    managePermission: 'manage:products',
    readPermission: 'read:products',
    importPermission: 'import:products',
    exportPermission: 'export:products'
  },
  {
    slug: 'locations',
    label: 'Locations',
    managePermission: 'manage:locations',
    readPermission: 'read:locations',
    exportPermission: 'export:locations'
  },
  {
    // Change Reasons are the renamed Credit Reasons — same backend permissions, which is how
    // ChangeReasonsController.cs is gated ([HasPermission(Permissions.ManageCreditReasons)]).
    slug: 'change-reasons',
    label: 'Change Reasons',
    managePermission: 'manage:credit_reasons',
    readPermission: 'read:credit_reasons'
  },
  {
    // Lists are the generic ListType/ListItem lookups (Activity Source, Payment Types, ...) —
    // the types are shard reference data, the items under them are tenant data.
    slug: 'lists',
    label: 'Lists',
    managePermission: 'manage:lists',
    readPermission: 'read:lists'
  },
  {
    slug: 'lots',
    label: 'Lots',
    managePermission: 'manage:lots',
    readPermission: 'read:lots',
    importPermission: 'import:lots',
    exportPermission: 'export:lots'
  },
  {
    // Coupons are pricing data, so they reuse the pricing permissions rather than having their own.
    slug: 'coupons',
    label: 'Coupons',
    managePermission: 'manage:pricing',
    readPermission: 'read:pricing',
    exportPermission: 'export:pricing'
  }
];

export function findMaintenanceTabBySlug(slug: string | undefined): MaintenanceTabAccess | undefined {
  if (!slug) return undefined;
  return MAINTENANCE_TABS.find((tab) => tab.slug === slug);
}

export function canAccessMaintenanceTab(hasPermission: (permissionCode: string) => boolean, tab: MaintenanceTabAccess): boolean {
  return hasPermission(tab.managePermission) || hasPermission(tab.readPermission);
}

export function hasAnyMaintenanceAccess(hasPermission: (permissionCode: string) => boolean): boolean {
  return MAINTENANCE_TABS.some((tab) => canAccessMaintenanceTab(hasPermission, tab));
}

/** Permission code(s) required for a tab's Import/Export action, as an array for use with
 * route guards / requiredPermissions props. Empty if the tab has no such action. */
export function maintenanceActionPermissions(slug: string, kind: 'import' | 'export'): string[] {
  const tab = findMaintenanceTabBySlug(slug);
  const code = kind === 'import' ? tab?.importPermission : tab?.exportPermission;
  return code ? [code] : [];
}

// Props shared by every Maintenance tab list. `canManage` gates the list's own "Add" action in its
// title bar; the Maintenance shell passes the active tab's manage permission.
export interface MaintenanceListProps {
  canManage?: boolean;
}
